/**
 * HHIConsentCheckbox
 *
 * Visible checkbox component for the PhonePe term-loan Personal Info page.
 * Renders: "I declare my household income exceeds ₹3 lakhs per annum"
 *
 * - User MUST tick this before Continue is enabled.
 * - If the user tries to continue without ticking, the prefill gate
 *   (LOS_HHICheckboxGate_V25 → /api/caprion/verify/hhi-checkbox-on-page/)
 *   returns 'failed' and shows a blocking toast.
 * - The checked value is stored in localState under the key
 *   'declares_hhi_above_3_lakh' so JourneyPage sends it to the gate endpoint.
 *
 * Uses plain HTML input[type=checkbox] — no shadcn dependency — to avoid
 * silent render failures from Radix UI type mismatches.
 *
 * Registry ID: LOS_HHICheckboxGate_V25
 */

import React, { useState, useEffect } from 'react';

interface HHIConsentCheckboxProps {
  value?: { declares_hhi_above_3_lakh?: boolean };
  onChange?: (data: { declares_hhi_above_3_lakh: boolean }) => void;
  disabled?: boolean;
}

const HHIConsentCheckbox: React.FC<HHIConsentCheckboxProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const [checked, setChecked] = useState<boolean>(
    value?.declares_hhi_above_3_lakh ?? false
  );
  const [touched, setTouched] = useState(false);

  // On mount: push the initial value into JourneyPage's localState so the gate
  // endpoint receives declares_hhi_above_3_lakh even if the user never touches
  // the checkbox (e.g. page reload with saved consent).
  useEffect(() => {
    onChange?.({ declares_hhi_above_3_lakh: checked });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally run once on mount only

  // Sync with parent value (e.g. when prefill loads saved consent)
  useEffect(() => {
    if (typeof value?.declares_hhi_above_3_lakh === 'boolean') {
      setChecked(value.declares_hhi_above_3_lakh);
      onChange?.({ declares_hhi_above_3_lakh: value.declares_hhi_above_3_lakh });
    }
  }, [value?.declares_hhi_above_3_lakh]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newChecked = e.target.checked;
    setChecked(newChecked);
    setTouched(true);
    onChange?.({ declares_hhi_above_3_lakh: newChecked });
  };

  const showWarning = touched && !checked;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <label
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          padding: '14px 16px',
          borderRadius: '10px',
          border: showWarning
            ? '1.5px solid #fca5a5'
            : checked
            ? '1.5px solid #86efac'
            : '1.5px solid #d1d5db',
          backgroundColor: showWarning
            ? '#fef2f2'
            : checked
            ? '#f0fdf4'
            : '#ffffff',
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'border-color 0.15s, background-color 0.15s',
        }}
      >
        <input
          type="checkbox"
          id="declares_hhi_above_3_lakh"
          checked={checked}
          onChange={handleChange}
          disabled={disabled}
          style={{
            width: '18px',
            height: '18px',
            marginTop: '1px',
            flexShrink: 0,
            cursor: disabled ? 'not-allowed' : 'pointer',
            accentColor: '#16a34a',
          }}
        />
        <span
          style={{
            fontSize: '14px',
            lineHeight: '1.45',
            color: disabled ? '#9ca3af' : '#111827',
            userSelect: 'none',
          }}
        >
          I declare my household income exceeds{' '}
          <strong style={{ fontWeight: 600 }}>₹3 lakhs per annum</strong>
        </span>
      </label>

      {showWarning && (
        <p
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '12px',
            color: '#dc2626',
            margin: '0 4px',
          }}
        >
          <svg
            style={{ width: '14px', height: '14px', flexShrink: 0 }}
            fill="none"
            stroke="currentColor"
            strokeWidth={2.5}
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
            />
          </svg>
          Please confirm your household income exceeds ₹3 lakhs per annum to proceed.
        </p>
      )}
    </div>
  );
};

export default HHIConsentCheckbox;
